/**
 * Tour Provider - infrastructure for guided walkthroughs.
 *
 * `runTour` stays an imperative async function because its caller is one: a
 * sidebar click handler that awaits the whole walkthrough. react-joyride, though,
 * is a component. The gap is bridged by the module-level store below, which
 * `runTour` writes to and `<TourMount>` reads through `useSyncExternalStore`, so
 * the tour library is mounted only while a tour is actually running -- and, being
 * behind `lazy()`, downloaded only then too. That keeps the property the previous
 * `await import("shepherd.js")` had.
 */

import i18next from "i18next";
import { Suspense, lazy, useSyncExternalStore } from "react";

export interface TourStep {
  id: string;
  text: string | (() => string);
  attachTo?: {
    element: string;
    on: "top" | "bottom" | "left" | "right" | "auto" | "center";
  };
  buttons?: Array<{
    text: string;
    action: (tour: {
      next: () => void;
      complete: () => void;
      cancel: () => void;
    }) => void;
    classes?: string;
  }>;
  /**
   * Resolved before the step is shown. Kept under its original name because the
   * step definitions are written against it; react-joyride's per-step `before`
   * hook has the same contract (the tour waits for the promise), so the runner
   * forwards one to the other.
   */
  beforeShowPromise?: () => Promise<void>;
}

export interface TourConfig {
  id: string;
  steps: TourStep[];
  beforeStart?: () => void;
  afterComplete?: () => void;
  afterCancel?: () => void;
}

export interface ActiveTour {
  config: TourConfig;
  /** Called by the runner exactly once, when the tour ends either way. */
  finish: (outcome: "complete" | "cancel") => void;
}

let activeTour: ActiveTour | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => activeTour;
// Never active during SSR: there is no localStorage to read the tour's status
// from, and no DOM to point at.
const getServerSnapshot = () => null;

/**
 * Run a tour programmatically. Resolves when the tour completes or is dismissed.
 */
export async function runTour(config: TourConfig): Promise<void> {
  if (typeof window === "undefined") return;
  // Two tours would render two overlays and fight over the same targets.
  if (activeTour) return;

  globalThis.tourRunning = true;

  // Navigates to the page the tour runs on.
  config.beforeStart?.();

  try {
    await i18next.loadNamespaces(["settings", "chat"]);

    // Let that navigation land before the first target is looked for. Steps also
    // carry a `targetWaitTimeout`, so this is not the only guard -- it just keeps
    // the first step from opening against the outgoing page.
    await new Promise((resolve) => setTimeout(resolve, 500));

    await new Promise<void>((resolve) => {
      activeTour = {
        config,
        finish: (outcome) => {
          activeTour = null;
          emit();
          if (outcome === "complete") {
            handleTourComplete(config.id);
            config.afterComplete?.();
          } else {
            handleTourSkip(config.id);
            config.afterCancel?.();
          }
          resolve();
        },
      };
      emit();
    });
  } catch (error) {
    console.error("Failed to start tour:", error);
    activeTour = null;
    emit();
    handleTourSkip(config.id);
  }
}

const TourRunner = lazy(() => import("#app/components/tours/tour-runner"));

/**
 * Mounted once, high in the tree. Renders nothing -- and pulls in nothing -- until
 * a tour is started.
 */
export function TourMount() {
  const tour = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  if (!tour) return null;

  return (
    <Suspense fallback={null}>
      <TourRunner tour={tour} />
    </Suspense>
  );
}

/**
 * Handle tour completion
 */
function handleTourComplete(tourId: string) {
  if (typeof window === "undefined") return;

  localStorage.setItem(`tour:${tourId}:completed`, "true");
  localStorage.setItem(`tour:${tourId}:completedAt`, Date.now().toString());
  localStorage.removeItem(`tour:${tourId}:skipped`);

  globalThis.tourRunning = false;
}

/**
 * Handle tour skip
 */
function handleTourSkip(tourId: string) {
  if (typeof window === "undefined") return;

  localStorage.setItem(`tour:${tourId}:skipped`, "true");
  localStorage.setItem(`tour:${tourId}:skippedAt`, Date.now().toString());

  // Clean up tour state
  cleanupTourState(tourId);

  globalThis.tourRunning = false;
}

/**
 * Clean up ephemeral tour state
 */
function cleanupTourState(tourId: string) {
  // Remove any tour-specific session data
  const tourSessionKey = `tour:${tourId}:session`;
  sessionStorage.removeItem(tourSessionKey);

  // Clear any tour flags in localStorage
  localStorage.removeItem(`tour:${tourId}:active`);
}

/**
 * Check if tour has been completed or skipped
 */
export function getTourStatus(tourId: string): {
  completed: boolean;
  skipped: boolean;
  completedAt?: number;
  skippedAt?: number;
} {
  if (typeof window === "undefined") {
    return { completed: false, skipped: false };
  }

  const completed = localStorage.getItem(`tour:${tourId}:completed`) === "true";
  const skipped = localStorage.getItem(`tour:${tourId}:skipped`) === "true";
  const completedAt = completed
    ? parseInt(localStorage.getItem(`tour:${tourId}:completedAt`) || "0", 10)
    : undefined;
  const skippedAt = skipped
    ? parseInt(localStorage.getItem(`tour:${tourId}:skippedAt`) || "0", 10)
    : undefined;

  return { completed, skipped, completedAt, skippedAt };
}

/**
 * Check if tour should be shown (not completed, not skipped)
 */
export function shouldShowTour(tourId: string): boolean {
  const status = getTourStatus(tourId);
  return !status.completed && !status.skipped;
}
